import React, {useEffect, useRef, useState} from 'react';
import {findPdvProductsByQueryString} from "../../utils/requestUtils";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import {useNavigate} from "react-router-dom";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";
import TextInputModal from "../common/TextInputModal";
import {showAlert} from "../../core/dialog";

const PdvSaleManagerItems = () => {
    const [products, setProducts] = useState([]);
    const [reload] = useState(false);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [queryString, setQueryString] = useState('');
    const inputQueryString = useRef(null);
    const [loading, setLoading] = useRecoilState(loadingAtom);
    const navigate = useNavigate();
    const saleCookie = localStorage.getItem('pdvSale');
    const newSale = {saleItems: []};
    const [sale, setSale] = useState(saleCookie ? JSON.parse(saleCookie) : newSale);
    const customerName = sale.customer !== undefined ? sale.customer.name : undefined;
    const [totalValue, setTotalValue] = useState(0);
    const [inputTextProductNameModalVisible, setInputTextProductNameModalVisible] = useState(false);
    const [inputTextCustomerNameModalVisible, setInputTextCustomerNameModalVisible] = useState(false);
    const [indexProductNoteChanging, setIndexProductNoteChanging] = useState(-1);

    function removeSpecialChars(str) {
        return str.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
    }

    async function makeQuery(query) {
        try {
            setLoading(true);
            let productWithDifferentNameToAdd = [];
            const fetchedProducts = await findPdvProductsByQueryString(query) || [];
            if (sale.saleItems !== undefined) {
                sale.saleItems.forEach((s) => {
                    for (let index = 0; index < fetchedProducts.length; index++) {
                        const p = fetchedProducts[index];
                        if (s.product.id === p.id) {
                            if (s.product.note !== p.note) {
                                const newProduct = {...p, note: s.product.note, quantity: s.quantity};
                                productWithDifferentNameToAdd = productWithDifferentNameToAdd.concat(newProduct);
                            } else {
                                p.quantity = s.quantity;
                            }
                        }
                    }
                });
            }
            for (let index = 0; index < productWithDifferentNameToAdd.length; index++) {
                const p = productWithDifferentNameToAdd[index];
                let indexToInsert = 0;
                for (let i = 0; i < fetchedProducts.length; i++) {
                    if (p.sort <= fetchedProducts[i].sort && p.name.includes(fetchedProducts[i].name)) {
                        indexToInsert = i;
                        break;
                    } else {
                        indexToInsert = i + 1;
                    }
                }
                fetchedProducts.splice(indexToInsert, 0, p);
            }
            setProducts(fetchedProducts);
            setFilteredProducts(JSON.parse(JSON.stringify(fetchedProducts)));
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { makeQuery(''); }, [reload]);

    useEffect(() => {
        localStorage.setItem('pdvSale', JSON.stringify(sale));
    }, [sale]);

    useEffect(() => {
        const newTotalValue = products.filter(p => p.quantity !== 0).reduce((acc, p) => {
            return acc + ((p.salePrice || 0) * (p.quantity ?? 0));
        }, 0);
        setTotalValue(newTotalValue);
    }, [products]);

    useEffect(() => {
        const filtered = products.filter(f => removeSpecialChars(f.name).toUpperCase().includes(queryString));
        setFilteredProducts(JSON.parse(JSON.stringify(filtered)));
    }, [queryString]);

    const handleInputChange = (e) => {
        setQueryString(removeSpecialChars(e.target.value).toUpperCase());
    };

    function doConfirm() {
        const saleItems = products
            .filter(p => p.quantity !== undefined && p.quantity !== 0)
            .map(p => ({product: p, quantity: p.quantity}));
        if (saleItems.length === 0) {
            showAlert('Nenhum item foi adicionado.');
            return;
        }
        const newSale = {table: sale.table, customerName: sale.customerName, customer: sale.customer, saleItems};
        localStorage.setItem('pdvSale', JSON.stringify(newSale));
        navigate('/pdv/confirm-items');
    }

    const onConfirm = async () => {
        setLoading(true);
        if (sale.customer === undefined) {
            setInputTextCustomerNameModalVisible(true);
        } else {
            doConfirm();
        }
        setLoading(false);
    };

    function changeProductNote(index) {
        setIndexProductNoteChanging(index);
        setInputTextProductNameModalVisible(true);
    }

    function onConfirmProductNote(note) {
        const product = {...filteredProducts[indexProductNoteChanging]};
        const newNote = note.toUpperCase().trim();
        let currentNote = (filteredProducts[indexProductNoteChanging].note || '').toUpperCase().trim();
        if (newNote !== currentNote) {
            let index = filteredProducts.findIndex(p => p.id === product.id && p.note === newNote);
            const newFilteredProducts = [...filteredProducts];
            const originalFilteredProduct = filteredProducts[indexProductNoteChanging];
            if (index === -1) {
                index = indexProductNoteChanging;
                const newProduct = {...product};
                if (!newProduct.quantity || newProduct.quantity === 0 || product.note === undefined) {
                    newProduct.quantity = originalFilteredProduct.quantity > 0 ? originalFilteredProduct.quantity : 1;
                    newProduct.note = newNote;
                    newFilteredProducts.splice(index, 0, {...newProduct});
                    newFilteredProducts[index + 1].quantity = 0;
                } else {
                    newFilteredProducts[index].note = newNote;
                }
            } else {
                newFilteredProducts[index].quantity = newFilteredProducts[index].quantity + (originalFilteredProduct.quantity > 0 ? originalFilteredProduct.quantity : 1);
                newFilteredProducts[indexProductNoteChanging].quantity = 0;
            }
            setFilteredProducts(newFilteredProducts);

            const newProducts = [...products];
            const origIdx = products.findIndex(p => p.id === product.id && (p.note === currentNote || (!p.note && currentNote === '')));
            const origProduct = products[origIdx];
            const prodIdx = products.findIndex(p => p.id === product.id && p.note === newNote);
            if (prodIdx === -1) {
                const newProduct = {...product};
                if (!newProduct.quantity || newProduct.quantity === 0 || product.note === undefined) {
                    newProduct.quantity = origProduct.quantity > 0 ? origProduct.quantity : 1;
                    newProduct.note = newNote;
                    newProducts.splice(origIdx, 0, {...newProduct});
                    newProducts[origIdx + 1].quantity = 0;
                } else {
                    newProducts[origIdx].note = newNote;
                }
            } else {
                newProducts[prodIdx].quantity = newProducts[prodIdx].quantity + (origProduct.quantity > 0 ? origProduct.quantity : 1);
                newProducts[origIdx].quantity = 0;
            }
            setProducts(newProducts);
        }
    }

    function onConfirmCustomerName(name) {
        sale.customerName = name;
        setSale({...sale});
        doConfirm();
    }

    return (
        <>
            {inputTextProductNameModalVisible ? (
                <TextInputModal
                    label={"Informe a observação para o item \r\n" + (filteredProducts[indexProductNoteChanging]?.name || '') + ":"}
                    value={filteredProducts[indexProductNoteChanging]?.note}
                    onConfirm={onConfirmProductNote}
                    onClose={() => setInputTextProductNameModalVisible(false)}
                    removeSpecialCharacters={true}/>
            ) : inputTextCustomerNameModalVisible ? (
                <TextInputModal
                    label={"Informe o nome do cliente:"}
                    value={sale.customerName || ''}
                    onConfirm={onConfirmCustomerName}
                    messageWhenEmpty="Nome inválido!"
                    onClose={() => setInputTextCustomerNameModalVisible(false)}
                    removeSpecialCharacters={true}/>
            ) : (
                <>
                    <div className="header-label">Pedido {customerName || 'Balcão'}</div>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}
                         className="input-container">
                        <input style={{width: '100%'}} ref={inputQueryString} type="text" value={queryString}
                               onChange={handleInputChange} onFocus={(e) => e.target.select()}/>
                        <button id="clearButton" onClick={() => { setQueryString(''); inputQueryString.current.focus(); }}>X</button>
                    </div>
                    <table style={{width: '100%'}}>
                        <colgroup>
                            <col style={{width: '300px'}}/>
                            <col style={{width: '50px'}}/>
                            <col style={{width: '50px'}}/>
                        </colgroup>
                        <thead>
                        <tr>
                            <th>Nome</th>
                            <th>Preço</th>
                            <th/><th/><th/>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredProducts.map((product, index) => (
                            <tr key={index}
                                style={{backgroundColor: product.quantity > 0 ? 'var(--warning-color)' : product.quantity < 0 ? 'var(--error-color)' : 'white'}}>
                                <td onClick={() => changeProductNote(index)}>
                                    <div>{`${product.name}${product.note ? ` ${product.note}` : ''}`}</div>
                                </td>
                                <td>
                                    <div style={{textAlign: 'right'}}>{(product.salePrice || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                </td>
                                <td>
                                    <button className="remove-button button-minus" onClick={() => {
                                        setProducts(prev => prev.map(item =>
                                            item.id === product.id && item.note === product.note
                                                ? {...item, quantity: (item.quantity || 0) - 1} : item));
                                        setFilteredProducts(prev => prev.map((item, i) =>
                                            i === index ? {...item, quantity: (item.quantity || 0) - 1} : item));
                                        setSale(prev => ({...prev, saleItems: products.filter(p => p.quantity !== 0).map(p => ({product: p, quantity: p.quantity}))}));
                                    }}>-</button>
                                </td>
                                <td>{product.quantity || 0}</td>
                                <td>
                                    <button className="button-plus" onClick={() => {
                                        setProducts(prev => prev.map(item =>
                                            item.id === product.id && item.note === product.note
                                                ? {...item, quantity: (item.quantity || 0) + 1} : item));
                                        setFilteredProducts(prev => prev.map((item, i) =>
                                            i === index ? {...item, quantity: (item.quantity || 0) + 1} : item));
                                        setSale(prev => ({...prev, saleItems: products.filter(p => p.quantity > 0).map(p => ({product: p, quantity: p.quantity}))}));
                                    }}>+</button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                        {filteredProducts.length === 0 && <tfoot><tr><td colSpan={5}>Nenhum item encontrado</td></tr></tfoot>}
                    </table>
                    <div style={{marginTop: '5px', alignItems: 'center'}}>
                        <div className="total-label" style={{textAlign: 'right'}}>
                            Valor Total: R$ {totalValue.toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </div>
                    </div>
                    <ActionButtonsManagerView>
                        <button onClick={onConfirm} disabled={loading}>Conferir</button>
                    </ActionButtonsManagerView>
                </>
            )}
        </>
    );
};

export default PdvSaleManagerItems;
